import { useEffect, useMemo, useState } from "react";
import { SITE, navLinks } from "@/mocks/site";
import type { NewsItem } from "@/mocks/types";

interface NavbarProps {
  news: NewsItem[];
  onRead: (item: NewsItem) => void;
}

export default function Navbar({ news, onRead }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return news
      .filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.excerpt.toLowerCase().includes(q) ||
          n.tags.some((t) => t.toLowerCase().includes(q))
      )
      .slice(0, 6);
  }, [query, news]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200/80 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[76px] max-w-[1400px] items-center justify-between px-4 md:px-6">
        {/* brand */}
        <a href="#top" className="group flex items-center gap-2.5" aria-label="返回首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 transition-transform duration-300 group-hover:rotate-12">
            <i className="ri-radar-line text-lg" aria-hidden="true"></i>
          </span>
          <span className="leading-none">
            <h1 className="block font-heading text-[19px] font-black leading-none tracking-tight text-foreground-900">
              {SITE.brand}
            </h1>
            <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-600">
              {SITE.brandEn}
            </span>
          </span>
        </a>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-900"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* actions */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              setSearchOpen((v) => !v);
              setMenuOpen(false);
            }}
            className={`flex h-9 w-9 items-center justify-center rounded-full border border-background-200/80 text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600 ${
              scrolled ? "bg-background-50" : "bg-background-50/70"
            }`}
            aria-label="搜索瓜讯"
          >
            <i className="ri-search-line text-lg" aria-hidden="true"></i>
          </button>
          <a
            href="#about"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            投稿爆料
            <i className="ri-send-plane-line text-sm" aria-hidden="true"></i>
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-200/80 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            aria-label="展开菜单"
            aria-expanded={menuOpen}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-4-line"} text-xl`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* search panel */}
      {searchOpen && (
        <div className="border-t border-background-200/80 bg-background-50/95 backdrop-blur-md">
          <div className="mx-auto max-w-[1400px] px-4 pb-4 pt-3 md:px-6">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100/70 px-4 py-2.5 focus-within:border-primary-300">
              <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                type="search"
                autoFocus
                placeholder="搜瓜：例如“空降”“新戏饼”“代言”"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                aria-label="搜索关键字"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} className="text-foreground-400 hover:text-foreground-700" aria-label="清空">
                  <i className="ri-close-circle-fill text-lg" aria-hidden="true"></i>
                </button>
              )}
            </div>
            {query.trim() && (
              <div className="mt-3">
                {results.length === 0 ? (
                  <p className="px-2 py-3 text-sm text-foreground-500">没有匹配的瓜讯，换个词试试～</p>
                ) : (
                  <ul className="space-y-1">
                    {results.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => {
                            onRead(r);
                            setSearchOpen(false);
                            setQuery("");
                          }}
                          className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                        >
                          <i className="ri-arrow-right-up-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                          <span className="flex-1">
                            <span className="block text-sm font-medium text-foreground-800">{r.title}</span>
                            <span className="mt-0.5 block text-xs text-foreground-500">
                              {r.tags.join(" · ")} · {r.date}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* mobile menu */}
      {menuOpen && (
        <div className="animate-fader border-t border-background-200/80 bg-background-50 lg:hidden">
          <nav className="mx-auto max-w-[1400px] px-4 py-3 md:px-6" aria-label="移动端导航">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between rounded-xl px-3 py-3 text-[15px] font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {l.label}
                <i className="ri-arrow-right-s-line text-xl text-foreground-400" aria-hidden="true"></i>
              </a>
            ))}
            <a
              href="#about"
              onClick={() => setMenuOpen(false)}
              className="mt-2 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-primary-500 px-4 py-3 text-[15px] font-semibold text-background-50 hover:bg-primary-600"
            >
              投稿爆料 <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}